
import { useEffect } from 'react';
import { clearImageCacheOnce } from '@/utils/serviceWorkerUtils';
import { createPlaceholderImage } from '@/utils/imageFormatSupport';
import { supabase } from '@/integrations/supabase/client';

interface ServiceDetailsLoaderProps {
  children: React.ReactNode;
  serviceId?: string | number;
}

export const ServiceDetailsLoader = ({ children, serviceId }: ServiceDetailsLoaderProps) => {
  useEffect(() => {
    // For service details, attempt to clear the image cache once per page load
    // This is especially important for newly migrated images
    clearImageCacheOnce();

    // Signal to the service worker that we're on the service details page
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'ON_SERVICE_DETAILS_PAGE'
      });
    }
    
    // If we have a serviceId, ensure placeholders exist for this service's images
    if (serviceId) {
      const ensurePlaceholders = async () => {
        try {
          // Get the service images using the direct relationship
          const { data: serviceImages } = await supabase
            .from('service_images')
            .select(`
              id,
              bucket,
              original_path
            `)
            .eq('service_id', Number(serviceId));
            
          if (!serviceImages || serviceImages.length === 0) return;
          
          // Process each image to ensure it has a placeholder
          for (const image of serviceImages) {
            if (image.bucket && image.original_path) {
              // Get the image URL
              const { data } = supabase.storage
                .from(image.bucket)
                .getPublicUrl(image.original_path);
                
              if (data?.publicUrl) {
                // Create a placeholder for this image if it doesn't exist already
                createPlaceholderImage(data.publicUrl, image.original_path)
                  .catch(err => console.warn('Error creating placeholder:', err));
              }
            }
          }
        } catch (error) {
          console.warn('Error ensuring placeholders:', error);
        }
      };
      
      // Don't block page rendering, do this in the background
      ensurePlaceholders();
    }
  }, [serviceId]);

  return <>{children}</>;
};
